import ReservationTimelineView, {
    Id,
    PPTimelineItem,
    ReservationTimelineViewProps,
} from '../components/ReservationTimelineView'
import {ReactCalendarGroupRendererProps} from 'react-calendar-timeline'
import React, {CSSProperties, ReactNode} from 'react'
import styled from 'styled-components'
import {BreakpointValues, DisplaySizeValue, FontSize} from '../../../styledComponents/Constants'
import {PlainLinkStyledButton} from '../../../styledComponents/Button'
import moment from 'moment-timezone'

type ItemType = 'myReservation' | 'blank' | 'loading'

export interface ResidentTimelineItem extends PPTimelineItem {
    type: ItemType
}

type excludeProps =
    'items'
    | 'areaWidth'
    | 'groupRenderer'
    | 'maxWidth'
    | 'dateHeaderProps'
    | 'initialVisibleStartDateTime'
    | 'dateHeaderPropsHour'
    | 'sidebarProps'

interface Props
    extends Omit<Partial<ReservationTimelineViewProps>, excludeProps> {
    items: ResidentTimelineItem[]
    areaWidth: number
    initialVisibleStartDateTime: moment.Moment
    onGroupClick?: (groupId: Id) => void
}

const ResidentReservationTimelineView: React.FC<Props> = props => {
    const {items, groups, areaWidth, initialVisibleStartDateTime} = props
    const firstGroupId = (groups && groups.length > 0) ?  groups[0].id : 'no-groups'
    return <TimeLineStyle>
        <ReservationTimelineView
            stackItems={false}
            areaWidth={areaWidth}
            maxWidth={{
                pc: DisplaySizeValue.ResidentWidthMax - (15 * 2),
                sp: BreakpointValues.md,
            }}
            groups={groups || []}
            items={items.map(item => convertToPPTimeLineItem(item, item.group === firstGroupId))}
            initialVisibleStartDateTime={initialVisibleStartDateTime}
            groupRenderer={(groupProps: ReactCalendarGroupRendererProps): ReactNode => {
                return (
                    <LeftAlignLink
                        onClick={
                            () => props.onGroupClick
                                ? props.onGroupClick(groupProps.group.id)
                                : undefined
                        }
                        title={`${groupProps.group.title}`}
                    >
                        {groupProps.group.title}
                    </LeftAlignLink>
                )
            }}
            dateHeaderPropsDay={{
                unit: 'day', height: 30,
                labelFormat: (date: any) => {
                    const d = Array.isArray(date) ? date[0] : date;
                    return moment(d.$d).tz('Asia/Tokyo').format('M/D(dd)')
                }
            }}
            dateHeaderPropsHour={{
                unit: 'hour', height: 30,
                labelFormat: (date: any) => {
                    const d = Array.isArray(date) ? date[0] : date;
                    return moment(d.$d).tz('Asia/Tokyo').format('H')
                }
            }}
            sidebarProps={{pcWidth: 245, spWidth: 154}}
            onVisibleRangeChanged={props.onVisibleRangeChanged}
        >
        </ReservationTimelineView>
    </TimeLineStyle>
}

ResidentReservationTimelineView.displayName = 'ResidentReservationTimelineView'
export default ResidentReservationTimelineView

const getStyle = (type: ItemType, isFirst: boolean): { style: CSSProperties } => {
    switch (type) {
        case 'blank':
            return {
                style: {
                    border: 'none',
                    backgroundColor: '#FFFFFF',
                    cursor: 'normal!important',
                    borderTop: isFirst ? 'none' : '1px solid #d9d9d9',
                },
            }
        case 'myReservation':
            return {
                style: {
                    border: 'none',
                    backgroundColor: 'rgb(255, 210, 150)',
                    cursor: 'normal!important',
                    borderTop: isFirst ? 'none' : '1px solid #d9d9d9',
                },
            }
        case 'loading':
            return {
                style: {
                    color: '#707070',
                    border: 'none',
                    backgroundColor: 'rgb(248, 248, 248)',
                    cursor: 'normal!important',
                    borderTop: isFirst ? 'none' : '1px solid #d9d9d9',
                },
            }
        default:
            throw new Error(`unknown type ${type}`)
    }
}

const convertToPPTimeLineItem = (item: ResidentTimelineItem, isFirst: boolean): PPTimelineItem => {
    const {type, ...rest} = item
    return {
        ...rest,
        itemProps: {
            ...getStyle(type, isFirst),
        },
    }
}

const LeftAlignLink = styled(PlainLinkStyledButton)`
  font-size: ${FontSize.Small};
  text-align: left;
  height: 100%;
  width: 100%;
  white-space: normal;
`


const TimeLineStyle = styled.div`
.react-calendar-timeline {
    font-size: ${FontSize.Small};
    border-top: 1px solid #d9d9d9;
    border-bottom: 1px solid #d9d9d9;
}
.react-calendar-timeline .rct-horizontal-lines .rct-hl-odd {
    background: rgba(0, 0, 0, 0);
}
.react-calendar-timeline .rct-dateHeader {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    border-bottom: none;
    cursor: default;
    background-color: #f3f8fc;
    border-left: 1px dashed #d9d9d9;
}
.react-calendar-timeline .rct-dateHeaderDay {
    display: flex;
    align-items: center;
    height: 100%;
    border-bottom: 1px solid #d9d9d9;
    cursor: default;
    background-color: #f3f8fc;
    border-left: 1px dashed #d9d9d9;
    justify-content: flex-start;
    padding: 0 10px 0 10px;
}

.react-calendar-timeline .rct-calendar-header {
    border-top: 1px solid #d9d9d9;
    border-bottom: none;
}
.react-calendar-timeline .rct-vertical-lines .rct-vl.rct-vl-first {
    //border-left-width: 0;
}
.react-calendar-timeline .rct-header-root {
    background: #FFFFFF;
    border-bottom: 1px solid #d9d9d9;
}
.react-calendar-timeline .rct-header-root > div:first-child {
    background-color: rgba(0,0,0,0);
    border-right: 1px solid #d9d9d9;
}
.react-calendar-timeline .rct-header-root > div:first-child > div {
    border: none;
}
.react-calendar-timeline .rct-sidebar {
  border-right: 1px solid #d9d9d9;
}
.react-calendar-timeline .rct-sidebar .rct-sidebar-row {
    padding: 0 4px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    box-sizing: border-box;
    margin: 0;
    border-top: 1px solid #d9d9d9;
    border-bottom: none;
    background: transparent;
}
.react-calendar-timeline .rct-sidebar .rct-sidebar-row:first-child {
    border-top: 1px solid transparent!important;
}
.react-calendar-timeline .rct-sidebar .rct-sidebar-row.rct-sidebar-row-odd {
    background: transparent;
}
.rct-outer {
  border-left: none;
  border-right: none;
}

.timeline-out-of-target {
  background: #b0b0b0!important;
}
.react-calendar-timeline .rct-scroll {
  border-top: none;
  background: #e3e3e3;
}
.react-calendar-timeline .rct-vertical-lines .rct-vl {
  position: absolute;
  z-index: 81!important;
  border-left: 1px dashed #d9d9d9;
}
.react-calendar-timeline .rct-horizontal-lines .rct-hl-even,
.react-calendar-timeline .rct-horizontal-lines .rct-hl-odd {
    border-top: 1px solid #d9d9d9;
    border-bottom: none;
    box-sizing: border-box;
}
.react-calendar-timeline .rct-horizontal-lines .rct-hl-even:first-child,
.react-calendar-timeline .rct-horizontal-lines .rct-hl-odd:first-child {
    border-top: none;
}
.react-calendar-timeline .rct-calendar-header {
    border: none;
}
.react-calendar-timeline .rct-vertical-lines .rct-vl.rct-day-6,
.react-calendar-timeline .rct-vertical-lines .rct-vl.rct-day-0 {
  background: none;
}
`
